import type { CompStage } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// pipelineView — shared "which tournament system does this comp use" detection
// for /divisions (pages/Divisions.tsx). (The legacy /groups page was deleted
// 2026-07-17 once no live comp used the GROUP_STAGE format.)
//
// GG Anvil has two coexisting systems: the LEGACY one (comp-level `format` /
// `stage`, skill-tier Divisions created by splitting the seeding table) and
// the NEWER ordered stage pipeline (CompStage rows — StagePipelinePanel.tsx).
// Both nav pages were built for the legacy system and render empty for a comp
// that has moved onto the pipeline; these two helpers are the detection this
// gap was missing.
//
// `hasStagePipeline` mirrors the EXACT signal AdvanceStageButton/
// useCompChecklist already use to tell the two systems apart (stageCount > 0
// — see useCompChecklist.ts's `stagesDone` and AdvanceStageButton.tsx's own
// header comment) — factored out here (rather than importing useCompChecklist
// itself) because that hook is org-scoped (needs an orgId) while /groups and
// /divisions each resolve their own compId from a local/workspace comp picker
// that may not match the caller's active org.
// ─────────────────────────────────────────────────────────────────────────────

export function hasStagePipeline(stages: CompStage[] | undefined): boolean {
  return !!stages && stages.length > 0;
}

/**
 * findDivisionsStage — picks the ONE DIVISIONS-type stage /groups and
 * /divisions should render standings for (a DIVISIONS stage is what stamps
 * the "Division A/B/C" labels the Matches board shows — see
 * lib/groupMatchesByStage.ts's header comment). Prefers a LIVE stage (the
 * only status with real generated fixtures/scoreboard data — see
 * DivisionsScoreboardPanel.tsx), so a comp whose divisions stage has since
 * ended (pipeline moved on to elimination) still finds it; falls back to any
 * other DIVISIONS stage (PENDING/ENDED) so the caller can render an honest
 * "not started yet" state instead of nothing when no LIVE one exists.
 */
export function findDivisionsStage(stages: CompStage[] | undefined): CompStage | undefined {
  if (!stages) return undefined;
  const divisionsStages = stages.filter((s) => s.type === 'DIVISIONS');
  return divisionsStages.find((s) => s.status === 'LIVE') ?? divisionsStages[0];
}

/**
 * E4 (comp-lifecycle-pipeline, incomplete-Swiss warnings) — pure decision for
 * StagePipelinePanel.tsx's End button: a LIVE SWISS stage whose
 * swissStatus.complete is false (some team hasn't resolved to IN/OUT yet —
 * either still mid-round, or an admin is about to cut it short) risks
 * seeding the next bracket stage from an unfinished cut, so End needs a
 * confirm step instead of firing immediately. Every other case (not SWISS,
 * not LIVE, `summary` not loaded yet, or already complete) returns null —
 * "no risk, no confirm needed" — matching the product posture that canEnd
 * itself stays permissive (warn, don't block).
 *
 * `summary` is optional/nullable because the caller's swiss-scoreboard query
 * may not have resolved yet (or a non-SWISS stage never fetches it at all) —
 * treated as "not proven incomplete" rather than defaulting to a scary
 * confirm on every LIVE Swiss card before its own data has loaded.
 */
export function swissEndRiskMessage(
  stage: Pick<CompStage, 'type' | 'status'>,
  summary: { complete: boolean } | null | undefined,
): string | null {
  if (stage.type !== 'SWISS' || stage.status !== 'LIVE') return null;
  if (!summary || summary.complete) return null;
  return "Swiss is not complete — ending now will seed the bracket from an unfinished state.";
}
